import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Ban, Clock, Package, Users } from 'lucide-react';
import { MonsterTargetRow } from './MonsterTargetRow';
import { EventChip } from './EventChip';
import { itemName, itemPath, mapPath, mapName } from '../../utils/worldFormat';
import { routeKindLabel } from '../../utils/worldQueries';
import { qualityLabel, routeQuality } from '../../utils/itemSources';
import type { ResolvedSource } from '../../utils/itemSources';
import { DataStatus } from './DataStatus';
import type { WorldData } from '../../types/world';
const PREVIEW = 6;
function Targets({
  w,
  s
}: {
  w: WorldData;
  s: ResolvedSource;
}) {
  const [all, setAll] = useState(false);
  if (!s.targets.length) return null;
  const shown = all ? s.targets : s.targets.slice(0, PREVIEW);
  const viaNames = s.route.viaItemIds.map(id => itemName(w, id));
  return <div className="mt-3" data-id="element-2191" data-mp="qndbi5z4yzvo">
      <p className="mb-2 text-xs font-semibold text-gray-400" data-id="element-2192" data-mp="qnwln0ijojz5">Monstruos que pueden soltarlo · {s.targets.length}</p>
      <ul className="grid gap-2" data-id="element-2193" data-mp="1pbmaf5qqlxtu">{shown.map(t => <MonsterTargetRow key={`${t.monsterId}-${t.mapId ?? ''}-${t.eventId ?? ''}-${t.isIndirect ? 1 : 0}`} w={w} t={t} viaNames={viaNames} data-id="element-2194" data-mp="1pf0b89jr5yz6" />)}</ul>
      {s.targets.length > PREVIEW && <button type="button" onClick={() => setAll(a => !a)} className="mt-2 h-10 text-sm font-semibold text-[#D4AF37] hover:text-[#F3E5AB]" data-id="element-2195" data-mp="1z8qxd7sepxbp">
          {all ? 'Mostrar menos' : `Ver los ${s.targets.length} monstruos`}
        </button>}
    </div>;
}

/** Fuente de obtención para jugadores: sin nombres de archivo ni IDs técnicos. */
export function SourceCard({
  w,
  s,
  nested = false
}: {
  w: WorldData;
  s: ResolvedSource;
  nested?: boolean;
}) {
  const disabled = s.status === 'disabled';
  return <li className={`rounded-xl md:rounded-2xl border p-3 md:p-4 text-[13px] md:text-base ${disabled ? 'border-white/5 bg-white/[0.01] opacity-80' : nested ? 'border-white/10 bg-[#050b14]/40' : 'border-white/10 bg-[#0A1628]'}`} data-id="element-2196" data-mp="1lcvkbikovgg8">
      <div className="flex flex-wrap items-center gap-2" data-id="element-2197" data-mp="ktjz698prbn9">
        <span className="rounded-full bg-[#D4AF37]/15 px-2 py-0.5 text-[11px] font-semibold text-[#F3E5AB]" data-id="element-2198" data-mp="krz48iihlqov">{s.label ?? routeKindLabel[s.kind] ?? s.kind}</span>
        <span className="font-semibold text-white" data-id="element-2199" data-mp="uitqvnznasau">{s.title}</span>
        {routeQuality(s.route, w) !== 'normal' && <span className="rounded-full border border-sky-300/30 px-2 py-0.5 text-[11px] font-semibold text-sky-100" title={!s.route.quality ? 'Calculado desde el perfil de opciones de la recompensa: si sale este resultado, sale con esta calidad.' : undefined} data-id="element-2200" data-mp="plafcvuc0tml">
            {qualityLabel[routeQuality(s.route, w)]}
            {routeQuality(s.route, w) === 'other' && s.route.quality ? `: ${s.route.quality}` : ''}
            {!s.route.quality ? ' · según perfil' : ''}
          </span>}
        {!disabled && !nested && <span className="ml-auto" data-id="element-2201" data-mp="1ia8y9v6a3zsy">
            {s.pending ? <DataStatus kind="partial" data-id="element-2202" data-mp="1psyoy5hb962x" /> : s.eventMaps.length || s.eventMapId !== undefined || s.targets.some(t => t.spawns.length) ? <DataStatus kind="location" withDate={false} data-id="element-2203" data-mp="1ususk3m3ivhy" /> : <DataStatus kind="route" withDate={false} data-id="element-2204" data-mp="1gt28us8zlfjh" />}
          </span>}
        {s.route.variantId && s.route.variantId !== s.route.itemId && w.variants.has(s.route.variantId) && <Link to={itemPath(s.route.variantId)} className="rounded-full border border-white/15 px-2 py-0.5 text-[11px] font-semibold text-gray-200 hover:border-[#D4AF37]/50" data-id="element-2205" data-mp="28khrycbc4k4y">
            Variante: {itemName(w, s.route.variantId)}
          </Link>}
      </div>

      {disabled && s.playerStatusNote && <p className="mt-2 flex items-start gap-1.5 text-sm text-red-200" data-id="element-2206" data-mp="23rf3ujpbu2w5">
          <Ban className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-2207" data-mp="2ri2e3bmpt4lv" />
          {s.playerStatusNote}
        </p>}
      {s.status === 'partial' && <p className="mt-2 flex items-start gap-1.5 text-sm text-amber-100" data-id="element-2208" data-mp="2wavn4bp7uz3r">
          <Users className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-2209" data-mp="18m0suoyj288q" />
          {s.playerProbability}
        </p>}
      {s.conditions && <p className="mt-1.5 text-sm text-gray-300" data-id="element-2210" data-mp="ym9ta8v4131s">{s.conditions}</p>}
      {s.status !== 'partial' && s.playerProbability && <p className="mt-1 text-sm text-gray-300" data-id="element-2211" data-mp="ulq1lktb2rn1">{s.playerProbability}</p>}

      {s.eventMapId !== undefined && <p className="mt-2 text-sm text-gray-400" data-id="element-2212" data-mp="1r9vz7nrt8jrt">
          Mapa: <Link to={mapPath(s.eventMapId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-2213" data-mp="1mcdzmnzongwh">{mapName(w, s.eventMapId)}</Link>
        </p>}

      {s.eventMaps.length > 0 && <div className="mt-3" data-id="element-2214" data-mp="1dklqqhqb4fu0">
          <p className="mb-2 text-xs font-semibold text-gray-400" data-id="element-2215" data-mp="1djam6fp1f41u">Premio del evento en</p>
          <ul className="flex flex-wrap gap-2" data-id="element-2216" data-mp="2a2hzck9r2wi7">
            {s.eventMaps.map(m => <li key={`${m.mapId}-${m.eventId ?? ''}`} className="flex items-center gap-2" data-id="element-2217" data-mp="259x89qi8axid">
                <Link to={mapPath(m.mapId)} className="inline-flex h-9 items-center rounded-full border border-white/15 px-3 text-xs font-semibold text-gray-200 hover:border-[#D4AF37]/50 hover:text-white" data-id="element-2218" data-mp="3nyrq2owe3zew">
                  {mapName(w, m.mapId)}
                </Link>
                {m.eventId && <EventChip w={w} eventId={m.eventId} data-id="element-2219" data-mp="5smxg08818fv" />}
              </li>)}
          </ul>
        </div>}

      <Targets w={w} s={s} data-id="element-2220" data-mp="aqewrfb73wu3" />

      {!disabled && (s.targets.length > 0 || s.eventMaps.length > 0) && <p className="mt-3 text-xs text-gray-500" data-id="element-2221" data-mp="fozw7cimichh">
          Combinación posible según la configuración del servidor: no es un drop garantizado
          {s.runtimeTested === true ? '.' : ' y no está comprobada dentro del juego.'}
        </p>}

      {s.via && s.via.length > 0 && <div className="mt-3 space-y-3 border-l-2 border-[#D4AF37]/30 pl-3" data-id="element-2222" data-mp="tj0opoizf39e">
          {s.via.map(v => {
        const shown = v.sources.filter(c => c.status !== 'disabled' && !c.pending);
        return <div key={v.itemId} data-id="element-2223" data-mp="2uk6w3g8812qo">
                <p className="flex flex-wrap items-center gap-1.5 text-sm text-gray-300" data-id="element-2224" data-mp="2pl434u5immz2">
                  <Package className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-2225" data-mp="349qg4p7o72n4" />
                  Primero consigue
                  <Link to={itemPath(v.itemId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4" data-id="element-2226" data-mp="2zii89xlznpc2">{v.name}</Link>
                </p>
                {shown.length > 0 && <ul className="mt-2 grid gap-2" data-id="element-2227" data-mp="3993d42s99q3f">{shown.map(c => <SourceCard key={c.key} w={w} s={c} nested data-id="element-2228" data-mp="398a2gqq6egg0" />)}</ul>}
              </div>;
      })}
        </div>}

      {s.pending && !disabled && <p className="mt-3 flex items-center gap-2 rounded-xl bg-white/[0.03] px-3 py-2.5 text-sm text-gray-300" data-id="element-2229" data-mp="3i98bjnfh1e5g">
          <Clock className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2230" data-mp="1svcwxxnv3bdf" />
          Ubicación de obtención pendiente de confirmar
        </p>}
    </li>;
}